import { useEffect, useState, Fragment } from "react";
import { Search, Download, RefreshCw, Cake, ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { btnPrimary, btnGhost, inputCls, fmtDate, downloadBlob } from "./shared";

const SEG_LABEL = { aktive: "Aktiv", inaktive: "Inaktiv", vip: "VIP", bursdag: "Bursdag snart" };
const SEG_CLS = {
  aktive: "bg-[#22c55e]/10 text-[#22c55e]",
  inaktive: "bg-white/5 text-[#A8A29E]",
  vip: "bg-[#D4AF37]/15 text-[#E5C564]",
  bursdag: "bg-pink-500/10 text-pink-300",
};

export default function Kunder() {
  const [items, setItems] = useState([]);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(null);

  const load = async (q = query) => {
    setLoading(true);
    try {
      const res = await api.get("/admin/customers", { params: { q } });
      setItems(res.data.items);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const t = setTimeout(() => load(query), 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  const exportCsv = async () => {
    try {
      const res = await api.get("/admin/export-marketing", { responseType: "blob" });
      downloadBlob(res.data, `kunder_${new Date().toISOString().slice(0, 10)}.csv`);
      toast.success("CSV eksportert");
    } catch {
      toast.error("Kunne ikke eksportere CSV");
    }
  };

  return (
    <div data-testid="kunder-page">
      <h1 className="font-serif-display text-3xl font-600 text-[#FDFBF7]">Kundedatabase</h1>
      <p className="mt-2 text-sm text-[#A8A29E]">{items.length} kunder</p>

      <div className="mt-6 flex flex-wrap items-center justify-between gap-4">
        <div className="relative min-w-[220px] flex-1">
          <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-[#A8A29E]" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Søk navn, e-post eller telefon…"
            data-testid="search-input"
            className={`${inputCls} !h-12 !pl-11`}
          />
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => load()} data-testid="refresh-btn" className={btnGhost}>
            <RefreshCw className="h-4 w-4" /> Oppdater
          </button>
          <button onClick={exportCsv} data-testid="export-csv-btn" className={btnPrimary}>
            <Download className="h-4 w-4" /> Eksporter (CSV)
          </button>
        </div>
      </div>

      <div className="mt-5 overflow-x-auto rounded-2xl border border-white/10 bg-[#282421]">
        <table className="w-full min-w-[900px] text-left text-sm" data-testid="customers-table">
          <thead>
            <tr className="border-b border-white/10 text-xs uppercase tracking-wide text-[#A8A29E]">
              <th className="px-4 py-4">Kunde</th>
              <th className="px-4 py-4">Kontaktinfo</th>
              <th className="px-4 py-4">Fødselsdato</th>
              <th className="px-4 py-4">Sist besøk</th>
              <th className="px-4 py-4 text-center">Brukte kuponger</th>
              <th className="px-4 py-4">Grupper</th>
              <th className="px-4 py-4">Registrert (dato og kl.)</th>
              <th className="px-4 py-4"></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8} className="px-4 py-10 text-center text-[#A8A29E]">Laster…</td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={8} className="px-4 py-10 text-center text-[#A8A29E]" data-testid="empty-state">Ingen kunder funnet.</td></tr>
            ) : (
              items.map((c) => (
                <Fragment key={c.id}>
                <tr className="border-b border-white/5 transition-colors hover:bg-white/5" data-testid={`customer-row-${c.id}`}>
                  <td className="px-4 py-4">
                    <p className="font-medium text-[#FDFBF7]">{c.fornavn}</p>
                    <p className="text-xs text-[#A8A29E]">{c.aldersgruppe}{c.alder != null ? ` · ${c.alder} år` : ""}</p>
                  </td>
                  <td className="px-4 py-4 text-[#A8A29E]">
                    <p>{c.epost}</p>
                    <p className="text-xs">{c.telefon}</p>
                  </td>
                  <td className="px-4 py-4 text-[#A8A29E]">
                    <span className="inline-flex items-center gap-1.5">
                      {c.fodselsdato || "—"}
                      {c.dager_til_bursdag != null && c.dager_til_bursdag <= 30 && <Cake className="h-3.5 w-3.5 text-pink-300" />}
                    </span>
                  </td>
                  <td className="px-4 py-4 text-xs text-[#A8A29E]" data-testid={`last-visit-${c.id}`}>{fmtDate(c.sist_besok)}</td>
                  <td className="px-4 py-4 text-center">
                    <span className="rounded-md bg-[#D4AF37]/10 px-2.5 py-1 font-semibold text-[#D4AF37]" data-testid={`used-count-${c.id}`}>
                      {c.antall_brukte}
                    </span>
                  </td>
                  <td className="px-4 py-4">
                    <div className="flex flex-wrap gap-1">
                      {c.segmenter.filter((s) => s !== "alle").map((s) => (
                        <span key={s} className={`rounded-full px-2 py-0.5 text-[0.65rem] font-medium ${SEG_CLS[s]}`}>{SEG_LABEL[s]}</span>
                      ))}
                    </div>
                  </td>
                  <td className="px-4 py-4 text-xs text-[#A8A29E]" data-testid={`registered-${c.id}`}>{fmtDate(c.opprettet)}</td>
                  <td className="px-2 py-4 text-right">
                    <button
                      onClick={() => setOpen(open === c.id ? null : c.id)}
                      data-testid={`toggle-details-${c.id}`}
                      className="rounded-lg p-1.5 text-[#A8A29E] transition-colors hover:bg-white/5 hover:text-[#D4AF37]"
                      aria-label="Vis detaljer"
                    >
                      {open === c.id ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                    </button>
                  </td>
                </tr>
                {open === c.id && (
                  <tr className="border-b border-white/5 bg-[#1A1715]/60" data-testid={`details-${c.id}`}>
                    <td colSpan={8} className="px-6 py-5">
                      <dl className="grid grid-cols-1 gap-x-8 gap-y-3 text-xs sm:grid-cols-2 lg:grid-cols-3">
                        {[
                          ["Fornavn", c.fornavn],
                          ["E-post", c.epost],
                          ["Telefon", c.telefon],
                          ["Fødselsdato", c.fodselsdato ? `${c.fodselsdato}${c.alder != null ? ` (${c.alder} år)` : ""}` : "—"],
                          ["Registrert første gang", fmtDate(c.opprettet)],
                          ["Vilkår godtatt", fmtDate(c.godtar_vilkar_tidspunkt)],
                          ["Samtykke SMS", c.samtykke_sms ? "Ja" : "Nei"],
                          ["Samtykke e-post", c.samtykke_epost ? "Ja" : "Nei"],
                          ["Samtykke gitt", fmtDate(c.markedsforing_samtykke_tidspunkt)],
                          ["Velkomstkupong", c.code || "—"],
                          ["Kupong gyldig fra", fmtDate(c.gyldig_fra)],
                          ["Kupong utløper", fmtDate(c.utloper)],
                          ["Velkomstkupong brukt", c.code_brukt ? fmtDate(c.code_brukt_tidspunkt) : "Nei"],
                          ["Velkomstkupong innløst", c.innlost_tidspunkt ? fmtDate(c.innlost_tidspunkt) : "Nei"],
                          ["Kampanjekuponger innløst", c.antall_kampanjekuponger],
                          ["Sist besøk", fmtDate(c.sist_besok)],
                        ].map(([k, v]) => (
                          <div key={k}>
                            <dt className="uppercase tracking-wide text-[#A8A29E]/70">{k}</dt>
                            <dd className="mt-0.5 text-sm text-[#FDFBF7]">{v}</dd>
                          </div>
                        ))}
                      </dl>
                    </td>
                  </tr>
                )}
                </Fragment>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
